import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Tooltip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
} from '@mui/material';
import {
  Download as DownloadIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ApiService } from '../services/api';
import { renderCompactLink } from '../utils/formatters';
import * as XLSX from 'xlsx';

// Helper function to truncate text
const truncateText = (text: string, maxLength: number = 30) => {
  if (!text) return '-';
  return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
};

const OutreachEmailResultsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session');

  const [domains, setDomains] = useState<any[]>([]);
  const [deliverableEmails, setDeliverableEmails] = useState<any[]>([]);
  const [riskyEmails, setRiskyEmails] = useState<any[]>([]);
  const [unknownEmails, setUnknownEmails] = useState<any[]>([]);
  const [undeliverableEmails, setUndeliverableEmails] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deliverableTotal, setDeliverableTotal] = useState(0);
  
  // Domain table pagination state
  const [domainDisplayCount, setDomainDisplayCount] = useState(100);
  const [isLoadingMoreDomains, setIsLoadingMoreDomains] = useState(false);
  
  // Deliverable emails table pagination state
  const [deliverableDisplayCount, setDeliverableDisplayCount] = useState(100);
  const [isLoadingMoreDeliverable, setIsLoadingMoreDeliverable] = useState(false);
  
  // Risky emails table pagination state
  const [riskyDisplayCount, setRiskyDisplayCount] = useState(100);
  const [isLoadingMoreRisky, setIsLoadingMoreRisky] = useState(false);
  
  // Unknown emails table pagination state
  const [unknownDisplayCount, setUnknownDisplayCount] = useState(100);
  const [isLoadingMoreUnknown, setIsLoadingMoreUnknown] = useState(false);
  
  // Undeliverable emails table pagination state
  const [undeliverableDisplayCount, setUndeliverableDisplayCount] = useState(100);
  const [isLoadingMoreUndeliverable, setIsLoadingMoreUndeliverable] = useState(false);

  // MailWhiz state
  const [showMailWhizModal, setShowMailWhizModal] = useState(false);
  const [mailWhizMode, setMailWhizMode] = useState<'sync' | 'intro'>('sync');
  const [mailWhizTemplates, setMailWhizTemplates] = useState<any[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [mailWhizListId, setMailWhizListId] = useState('');
  const [isMailWhizProcessing, setIsMailWhizProcessing] = useState(false);
  const [mailWhizResult, setMailWhizResult] = useState<any>(null);

  useEffect(() => {
    if (!sessionId) {
      setError("Session ID not found in URL.");
      setIsLoading(false);
      return;
    }

    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        // Check if we have resumedDomainIds in URL (from resume session)
        const resumedDomainIds = searchParams.get('resumedDomainIds');
        // Fetch all deliverable emails (no pagination, we'll handle it client-side)
        const result = await ApiService.getEmailResults(sessionId, 1, 10000, resumedDomainIds || undefined);
        setDomains(result.domains);
        setDeliverableEmails(result.deliverableEmails);
        setDeliverableTotal(result.deliverableTotal || result.deliverableEmails.length || 0);
        setRiskyEmails(result.riskyEmails);
        setUnknownEmails(result.unknownEmails || []);
        setUndeliverableEmails(result.undeliverableEmails);
      } catch (err: any) {
        let errorMessage = "Failed to fetch email results.";
        
        if (err instanceof Error) {
          // Check if it's a network/server error
          if (err.message.includes('Failed to fetch') || 
              err.message.includes('NetworkError') ||
              err.message.includes('Network request failed') ||
              err.name === 'TypeError') {
            errorMessage = 'Server not running. Please start the server';
          } else {
            errorMessage = err.message || errorMessage;
          }
        }
        
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [sessionId, searchParams]);

  const handleBack = () => {
    // Preserve resumedDomainIds if present (for resumed sessions)
    const resumedDomainIds = searchParams.get('resumedDomainIds');
    const url = resumedDomainIds 
      ? `/outreach/validation?session=${sessionId}&resumedDomainIds=${resumedDomainIds}`
      : `/outreach/validation?session=${sessionId}`;
    navigate(url);
  };

  const handleLoadMoreDomains = () => {
    setIsLoadingMoreDomains(true);
    // Simulate loading delay
    setTimeout(() => {
      setDomainDisplayCount(prev => Math.min(prev + 100, domains.length));
      setIsLoadingMoreDomains(false);
    }, 300);
  };

  const handleLoadMoreDeliverable = () => {
    setIsLoadingMoreDeliverable(true);
    // Simulate loading delay
    setTimeout(() => {
      setDeliverableDisplayCount(prev => Math.min(prev + 100, deliverableEmails.length));
      setIsLoadingMoreDeliverable(false);
    }, 300);
  };

  const handleLoadMoreRisky = () => {
    setIsLoadingMoreRisky(true);
    // Simulate loading delay
    setTimeout(() => {
      setRiskyDisplayCount(prev => Math.min(prev + 100, riskyEmails.length));
      setIsLoadingMoreRisky(false);
    }, 300);
  };

  const handleLoadMoreUnknown = () => {
    setIsLoadingMoreUnknown(true);
    // Simulate loading delay
    setTimeout(() => {
      setUnknownDisplayCount(prev => Math.min(prev + 100, unknownEmails.length));
      setIsLoadingMoreUnknown(false);
    }, 300);
  };

  const handleLoadMoreUndeliverable = () => {
    setIsLoadingMoreUndeliverable(true);
    // Simulate loading delay
    setTimeout(() => {
      setUndeliverableDisplayCount(prev => Math.min(prev + 100, undeliverableEmails.length));
      setIsLoadingMoreUndeliverable(false);
    }, 300);
  };

  // Download handlers for each table
  const handleDownloadCompanies = async () => {
    if (!sessionId) {
      alert('Session ID not found.');
      return;
    }
    try {
      setIsLoading(true);
      const resumedDomainIds = searchParams.get('resumedDomainIds');
      const blob = await ApiService.exportOutreachCompanies(sessionId, resumedDomainIds || undefined);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().split('T')[0];
      link.download = `companies_${sessionId}_${timestamp}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Error downloading companies:', err);
      alert(`Failed to download companies: ${err.message || 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadDeliverable = async () => {
    if (!sessionId) {
      alert('Session ID not found.');
      return;
    }
    try {
      setIsLoading(true);
      const resumedDomainIds = searchParams.get('resumedDomainIds');
      const blob = await ApiService.exportOutreachDeliverableEmails(sessionId, resumedDomainIds || undefined);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().split('T')[0];
      link.download = `deliverable_emails_${sessionId}_${timestamp}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Error downloading deliverable emails:', err);
      alert(`Failed to download deliverable emails: ${err.message || 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadRisky = async () => {
    if (!sessionId) {
      alert('Session ID not found.');
      return;
    }
    try {
      setIsLoading(true);
      const resumedDomainIds = searchParams.get('resumedDomainIds');
      const blob = await ApiService.exportOutreachRiskyEmails(sessionId, resumedDomainIds || undefined);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().split('T')[0];
      link.download = `risky_emails_${sessionId}_${timestamp}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Error downloading risky emails:', err);
      alert(`Failed to download risky emails: ${err.message || 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadUndeliverable = async () => {
    if (!sessionId) {
      alert('Session ID not found.');
      return;
    }
    try {
      setIsLoading(true);
      const resumedDomainIds = searchParams.get('resumedDomainIds');
      const blob = await ApiService.exportOutreachUndeliverableEmails(sessionId, resumedDomainIds || undefined);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().split('T')[0];
      link.download = `undeliverable_emails_${sessionId}_${timestamp}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Error downloading undeliverable emails:', err);
      alert(`Failed to download undeliverable emails: ${err.message || 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadUnknown = async () => {
    if (!sessionId) {
      alert('Session ID not found.');
      return;
    }
    try {
      setIsLoading(true);
      const resumedDomainIds = searchParams.get('resumedDomainIds');
      const blob = await ApiService.exportOutreachUnknownEmails(sessionId, resumedDomainIds || undefined);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().split('T')[0];
      link.download = `unknown_emails_${sessionId}_${timestamp}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Error downloading unknown emails:', err);
      alert(`Failed to download unknown emails: ${err.message || 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenMailWhizModal = async (mode: 'sync' | 'intro') => {
    setMailWhizMode(mode);
    setShowMailWhizModal(true);
    setMailWhizResult(null);
    
    if (mode === 'intro' && mailWhizTemplates.length === 0) {
      try {
        const result = await ApiService.getMailWhizTemplates();
        if (result.status === 'success' && result.data && result.data.records) {
          setMailWhizTemplates(result.data.records);
        }
      } catch (err) {
        console.error('Error fetching MailWhiz templates:', err);
      }
    }
  };

  const handleMailWhizAction = async () => {
    setIsMailWhizProcessing(true);
    setMailWhizResult(null);
    
    const emails = deliverableEmails.map(e => e.email).filter(Boolean);
    
    try {
      let result;
      if (mailWhizMode === 'sync') {
        if (!mailWhizListId) {
          alert('Please enter a List UID');
          setIsMailWhizProcessing(false);
          return;
        }
        result = await ApiService.syncMailWhizList(emails, mailWhizListId, sessionId || undefined);
      } else {
        if (!selectedTemplate) {
          alert('Please select a template');
          setIsMailWhizProcessing(false);
          return;
        }
        result = await ApiService.sendMailWhizIntro(emails, selectedTemplate, sessionId || undefined);
      }
      
      setMailWhizResult(result);
      // Refresh data to show "in_mailer" status
      const resumedDomainIds = searchParams.get('resumedDomainIds');
      const updatedData = await ApiService.getEmailResults(sessionId!, 1, 10000, resumedDomainIds || undefined);
      setDeliverableEmails(updatedData.deliverableEmails);
      
    } catch (err: any) {
      console.error('MailWhiz Error:', err);
      alert(`Error: ${err.message || 'Action failed'}`);
    } finally {
      setIsMailWhizProcessing(false);
    }
  };

  const handleDownloadAllExcel = async () => {
    try {
      if (!sessionId) {
        alert('Session ID not found.');
        return;
      }

      setIsLoading(true);
      const resumedDomainIds = searchParams.get('resumedDomainIds');
      const blob = await ApiService.exportOutreachExcel(sessionId, resumedDomainIds || undefined);
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().split('T')[0];
      link.download = `outreach_full_results_${sessionId}_${timestamp}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Error exporting to Excel:', err);
      alert(`Failed to export Excel: ${err.message || 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };


  if (isLoading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mt: 2 }}
        >
          BACK
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header Section */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            Email Results
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            View validated email results by category
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            color="success"
            startIcon={<DownloadIcon />}
            size="small"
            onClick={handleDownloadAllExcel}
            sx={{ fontWeight: 'bold' }}
          >
            DOWNLOAD ALL (EXCEL)
          </Button>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            size="small"
            onClick={handleBack}
          >
            BACK
          </Button>
        </Box>
      </Box>

      {/* Company Table */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Company
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadCompanies}
            disabled={isLoading}
          >
            Download
          </Button>
        </Box>
        <Paper sx={{ 
          flexShrink: 0,
          backgroundColor: 'white',
          width: '100%',
          maxWidth: '100%',
          overflowX: 'hidden',
          mb: 2
        }}>
          <TableContainer 
            sx={{ 
              maxHeight: 'none',
              overflowY: 'auto',
              overflowX: 'auto'
            }}
          >
            <Table 
              stickyHeader
              sx={{ 
                minWidth: 1800,
                '& .MuiTableCell-root': { 
                  padding: '6px 6px',
                  whiteSpace: 'nowrap'
                }
              }}
            >
              <TableHead>
                <TableRow>
                  {/* Match old domains table order: all company columns in order */}
                  <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>Website</TableCell>
                  <TableCell sx={{ minWidth: 200, fontWeight: 'bold', backgroundColor: '#fff' }}>Company Name</TableCell>
                  <TableCell sx={{ minWidth: 200, fontWeight: 'bold', backgroundColor: '#fff' }}>Clean Name</TableCell>
                  <TableCell sx={{ minWidth: 200, fontWeight: 'bold', backgroundColor: '#fff' }}>Notes</TableCell>
                  <TableCell sx={{ minWidth: 300, fontWeight: 'bold', backgroundColor: '#fff' }}>Description</TableCell>
                  <TableCell align="right" sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>Unsubscribe</TableCell>
                  <TableCell align="right" sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>Inmailer</TableCell>
                  <TableCell sx={{ minWidth: 100, fontWeight: 'bold', backgroundColor: '#fff' }}>State</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>Location</TableCell>
                  <TableCell sx={{ minWidth: 100, fontWeight: 'bold', backgroundColor: '#fff' }}>Country</TableCell>
                  <TableCell sx={{ minWidth: 200, fontWeight: 'bold', backgroundColor: '#fff' }}>Company Address</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>Snov ID</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>Industry Tag</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>Industry Tag Two</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>Industry Tag Three</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>Industry Tag Four</TableCell>
                  <TableCell align="right" sx={{ minWidth: 70, fontWeight: 'bold', backgroundColor: '#fff' }}>
                    <Tooltip title="Deliverable" arrow>
                      <span>Del</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right" sx={{ minWidth: 70, fontWeight: 'bold', backgroundColor: '#fff' }}>
                    <Tooltip title="Risky" arrow>
                      <span>Ris</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right" sx={{ minWidth: 70, fontWeight: 'bold', backgroundColor: '#fff' }}>
                    <Tooltip title="Undeliverable" arrow>
                      <span>Und</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right" sx={{ minWidth: 70, fontWeight: 'bold', backgroundColor: '#fff' }}>
                    <Tooltip title="Bounce" arrow>
                      <span>Bou</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ minWidth: 60, fontWeight: 'bold', backgroundColor: '#fff' }}>LI</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>Employees on LinkedIn</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>LinkedIn ID</TableCell>
                  <TableCell sx={{ minWidth: 60, fontWeight: 'bold', backgroundColor: '#fff' }}>TW</TableCell>
                  <TableCell sx={{ minWidth: 60, fontWeight: 'bold', backgroundColor: '#fff' }}>FB</TableCell>
                  <TableCell sx={{ minWidth: 60, fontWeight: 'bold', backgroundColor: '#fff' }}>IG</TableCell>
                  <TableCell sx={{ minWidth: 60, fontWeight: 'bold', backgroundColor: '#fff' }}>YT</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>License</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>Phone</TableCell>
                  <TableCell align="right" sx={{ minWidth: 100, fontWeight: 'bold', backgroundColor: '#fff' }}>Unknown</TableCell>
                  <TableCell align="right" sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>Not Verified</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>Suspicious</TableCell>
                  <TableCell sx={{ minWidth: 100, fontWeight: 'bold', backgroundColor: '#fff' }}>Type</TableCell>
                  <TableCell sx={{ minWidth: 100, fontWeight: 'bold', backgroundColor: '#fff' }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {domains.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={33} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No companies found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  domains.slice(0, domainDisplayCount).map((domain) => (
                    <TableRow key={domain.id} hover>
                      <TableCell sx={{ fontWeight: 'medium' }}>{renderCompactLink(domain.website || domain.domain, 'website')}</TableCell>
                      <TableCell>{domain.name || '-'}</TableCell>
                      <TableCell>{domain.clean_name || '-'}</TableCell>
                      <TableCell>
                        <Tooltip title={String(domain.note || '')} arrow>
                          <span>{truncateText(String(domain.note || ''), 30)}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={String(domain.description || '')} arrow>
                          <span>{truncateText(String(domain.description || ''), 30)}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">{domain.unsubscribe ?? '-'}</TableCell>
                      <TableCell align="right">
                        {domain.in_mailer ? (
                          <Box sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', px: 1, py: 0.5, borderRadius: 1, display: 'inline-block', fontSize: '11px', fontWeight: 'bold' }}>
                            ADDED
                          </Box>
                        ) : (
                          <Box sx={{ bgcolor: '#f5f5f5', color: '#757575', px: 1, py: 0.5, borderRadius: 1, display: 'inline-block', fontSize: '11px' }}>
                            PENDING
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>{domain.state_name || '-'}</TableCell>
                      <TableCell>{domain.location || '-'}</TableCell>
                      <TableCell>{domain.country_name || '-'}</TableCell>
                      <TableCell>
                        <Tooltip title={String(domain.company_address || '')} arrow>
                          <span>{truncateText(String(domain.company_address || ''), 30)}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>{domain.snov_id || '-'}</TableCell>
                      <TableCell>{domain.industry_tag_name || '-'}</TableCell>
                      <TableCell>{domain.industry_tag_two_name || '-'}</TableCell>
                      <TableCell>{domain.industry_tag_three_name || '-'}</TableCell>
                      <TableCell>{domain.industry_tag_four_name || '-'}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>{domain.deliverable || domain.deliverableCount || 0}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>{domain.risky || domain.riskyCount || 0}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>{domain.undeliverable || domain.undeliverableCount || 0}</TableCell>
                      <TableCell align="right">{domain.bounce ?? '-'}</TableCell>
                      <TableCell>{renderCompactLink(domain.Linkedin, 'linkedin')}</TableCell>
                      <TableCell>{domain.employees_onlinkedin || '-'}</TableCell>
                      <TableCell>{domain.linkedin_id || '-'}</TableCell>
                      <TableCell>{renderCompactLink(domain.twitter, 'twitter')}</TableCell>
                      <TableCell>{renderCompactLink(domain.Facebook, 'Facebook')}</TableCell>
                      <TableCell>{renderCompactLink(domain.ig, 'ig')}</TableCell>
                      <TableCell>{renderCompactLink(domain.Youtube, 'Youtube')}</TableCell>
                      <TableCell>{domain.license || '-'}</TableCell>
                      <TableCell>{domain.phone || '-'}</TableCell>
                      <TableCell align="right">{domain.unknown ?? '-'}</TableCell>
                      <TableCell align="right">{domain.not_verified ?? '-'}</TableCell>
                      <TableCell>{domain.suspicious || '-'}</TableCell>
                      <TableCell>{domain.type_ || '-'}</TableCell>
                      <TableCell>{domain.status || '-'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
        
        {/* Load More Section - Right Aligned */}
        {domains.length > 0 && (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 2,
            mb: 3, 
            width: '100%',
            maxWidth: '100%',
            overflowX: 'hidden'
          }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
              {Math.min(domainDisplayCount, domains.length)} of {domains.length}
            </Typography>
            
            {isLoadingMoreDomains ? (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center',
                gap: 1
              }}>
                <CircularProgress size={16} />
                <Typography variant="caption" color="text.secondary">
                  Loading...
                </Typography>
              </Box>
            ) : domainDisplayCount < domains.length ? (
              <button 
                onClick={handleLoadMoreDomains}
                style={{
                  padding: '6px 16px',
                  backgroundColor: 'transparent',
                  color: '#1976d2',
                  border: '1px solid #1976d2',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: '500',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => {
                  (e.target as HTMLButtonElement).style.backgroundColor = '#1976d2';
                  (e.target as HTMLButtonElement).style.color = 'white';
                }}
                onMouseOut={(e) => {
                  (e.target as HTMLButtonElement).style.backgroundColor = 'transparent';
                  (e.target as HTMLButtonElement).style.color = '#1976d2';
                }}
              >
                Load More
              </button>
            ) : (
              <Typography variant="caption" color="text.secondary">
                No more records
              </Typography>
            )}
          </Box>
        )}
      </Box>

      {/* Deliverable Emails Table */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Deliverable ({deliverableTotal})
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              color="primary"
              onClick={() => handleOpenMailWhizModal('sync')}
              disabled={isLoading || deliverableEmails.length === 0}
            >
              Sync to List
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="secondary"
              onClick={() => handleOpenMailWhizModal('intro')}
              disabled={isLoading || deliverableEmails.length === 0}
            >
              Send Intro
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadDeliverable}
              disabled={isLoading}
            >
              Download
            </Button>
          </Box>
        </Box>
        <Paper sx={{ 
          flexShrink: 0,
          backgroundColor: 'white',
          width: '100%',
          maxWidth: '100%',
          overflowX: 'hidden',
          mb: 2
        }}>
          <TableContainer 
            sx={{ 
              maxHeight: 'none',
              overflowY: 'auto',
              overflowX: 'auto'
            }}
          >
            <Table 
              stickyHeader
              sx={{ 
                minWidth: 3000,
                '& .MuiTableCell-root': { 
                  padding: '6px 6px',
                  whiteSpace: 'nowrap'
                }
              }}
            >
              <TableHead>
                <TableRow>
                  {/* Match people table order: First Name, Last Name, Email first, then rest in order */}
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>First Name</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>Last Name</TableCell>
                  <TableCell sx={{ minWidth: 200, fontWeight: 'bold', backgroundColor: '#fff' }}>Email</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>Company Name</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>Title</TableCell>
                  <TableCell sx={{ minWidth: 200, fontWeight: 'bold', backgroundColor: '#fff' }}>Notes</TableCell>
                  <TableCell sx={{ minWidth: 300, fontWeight: 'bold', backgroundColor: '#fff' }}>Description</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>Unsubscribe</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>In Mailer</TableCell>
                  <TableCell sx={{ minWidth: 100, fontWeight: 'bold', backgroundColor: '#fff' }}>State</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>Location</TableCell>
                  <TableCell sx={{ minWidth: 200, fontWeight: 'bold', backgroundColor: '#fff' }}>California Location</TableCell>
                  <TableCell sx={{ minWidth: 100, fontWeight: 'bold', backgroundColor: '#fff' }}>Country</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>Area</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>Date Added</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>Last Emailed</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>Industry Tag</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>Industry Tag Two</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>Industry Tag Three</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>Industry Tag Four</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>Verification Status</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>Verification Date</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>LinkedIn ID</TableCell>
                  <TableCell sx={{ minWidth: 60, fontWeight: 'bold', backgroundColor: '#fff' }}>LI</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>LinkedIn Group ID</TableCell>
                  <TableCell sx={{ minWidth: 60, fontWeight: 'bold', backgroundColor: '#fff' }}>TW</TableCell>
                  <TableCell sx={{ minWidth: 60, fontWeight: 'bold', backgroundColor: '#fff' }}>FB</TableCell>
                  <TableCell sx={{ minWidth: 60, fontWeight: 'bold', backgroundColor: '#fff' }}>IG</TableCell>
                  <TableCell sx={{ minWidth: 140, fontWeight: 'bold', backgroundColor: '#fff' }}>Brand Follower</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>Username</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>P Tag 1</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>P Tag 2</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>P Tag 3</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>P Tag 4</TableCell>
                  <TableCell sx={{ minWidth: 100, fontWeight: 'bold', backgroundColor: '#fff' }}>Type</TableCell>
                  <TableCell sx={{ minWidth: 140, fontWeight: 'bold', backgroundColor: '#fff' }}>Company Type</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>License</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>Created At</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>Date Add in Mailer</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>Date Updated</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>Last Clicked</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>Last Opened</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>News THC</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>News CBD</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>News INV</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>News MED</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>Office Phone</TableCell>
                  <TableCell sx={{ minWidth: 100, fontWeight: 'bold', backgroundColor: '#fff' }}>Status</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>Leadleaper ID</TableCell>
                  <TableCell sx={{ minWidth: 100, fontWeight: 'bold', backgroundColor: '#fff' }}>B2B/B2C</TableCell>
                  <TableCell sx={{ minWidth: 100, fontWeight: 'bold', backgroundColor: '#fff' }}>Bounce</TableCell>
                  <TableCell sx={{ minWidth: 80, fontWeight: 'bold', backgroundColor: '#fff' }}>ID</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {deliverableEmails.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={50} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No deliverable emails found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  deliverableEmails.slice(0, deliverableDisplayCount).map((email) => (
                    <TableRow key={email.id} hover>
                      <TableCell>{email.first_name || '-'}</TableCell>
                      <TableCell>{email.last_name || '-'}</TableCell>
                      <TableCell sx={{ fontWeight: 'medium' }}>{email.email || '-'}</TableCell>
                      <TableCell>{email.company_name || '-'}</TableCell>
                      <TableCell>{email.title || '-'}</TableCell>
                      <TableCell>
                        <Tooltip title={String(email.notes || '')} arrow>
                          <span>{truncateText(String(email.notes || ''), 30)}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={String(email.Description || '')} arrow>
                          <span>{truncateText(String(email.Description || ''), 30)}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>{email.unsubscribe ?? '-'}</TableCell>
                      <TableCell>
                        {email.in_mailer ? (
                          <Box sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', px: 1, py: 0.5, borderRadius: 1, display: 'inline-block', fontSize: '11px', fontWeight: 'bold' }}>
                            ADDED
                          </Box>
                        ) : (
                          <Box sx={{ bgcolor: '#f5f5f5', color: '#757575', px: 1, py: 0.5, borderRadius: 1, display: 'inline-block', fontSize: '11px' }}>
                            PENDING
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>{email.state_name || '-'}</TableCell>
                      <TableCell>{email.location || '-'}</TableCell>
                      <TableCell>{email.California_location || '-'}</TableCell>
                      <TableCell>{email.country_name || '-'}</TableCell>
                      <TableCell>{email.area || '-'}</TableCell>
                      <TableCell>{email.date_added || '-'}</TableCell>
                      <TableCell>{email.last_emailed || '-'}</TableCell>
                      <TableCell>{email.industry_name || email.industry_tag_name || '-'}</TableCell>
                      <TableCell>{email.industry_tag_two_name || '-'}</TableCell>
                      <TableCell>{email.industry_tag_three_name || '-'}</TableCell>
                      <TableCell>{email.industry_tag_four_name || '-'}</TableCell>
                      <TableCell>{email.verification_status_name || '-'}</TableCell>
                      <TableCell>{email.verification_date || '-'}</TableCell>
                      <TableCell>{email.linkedin_id || '-'}</TableCell>
                      <TableCell>{renderCompactLink(email.linkedin, 'linkedin')}</TableCell>
                      <TableCell>{email.linkedin_group_id || '-'}</TableCell>
                      <TableCell>{renderCompactLink(email.twitter, 'twitter')}</TableCell>
                      <TableCell>{renderCompactLink(email.facebook, 'facebook')}</TableCell>
                      <TableCell>{renderCompactLink(email.ig, 'ig')}</TableCell>
                      <TableCell>{email.brand_follower || '-'}</TableCell>
                      <TableCell>{email.username || '-'}</TableCell>
                      <TableCell>{email.p_tag_1_name || '-'}</TableCell>
                      <TableCell>{email.p_tag_2_name || '-'}</TableCell>
                      <TableCell>{email.p_tag_3_name || '-'}</TableCell>
                      <TableCell>{email.p_tag_4_name || '-'}</TableCell>
                      <TableCell>{email.type_ || '-'}</TableCell>
                      <TableCell>{email.company_type || '-'}</TableCell>
                      <TableCell>{email.license || '-'}</TableCell>
                      <TableCell>{email.created_at || '-'}</TableCell>
                      <TableCell>{email.date_add_in_mailer || '-'}</TableCell>
                      <TableCell>{email.date_updated || '-'}</TableCell>
                      <TableCell>{email.last_clicked || '-'}</TableCell>
                      <TableCell>{email.last_opened || '-'}</TableCell>
                      <TableCell>{email.news_thc ?? '-'}</TableCell>
                      <TableCell>{email.news_cbd ?? '-'}</TableCell>
                      <TableCell>{email.news_inv ?? '-'}</TableCell>
                      <TableCell>{email.news_med ?? '-'}</TableCell>
                      <TableCell>{email.oficine_phone || '-'}</TableCell>
                      <TableCell>{email.status || '-'}</TableCell>
                      <TableCell>{email.leadleaper_id || '-'}</TableCell>
                      <TableCell>{email.b2b_or_b2c || '-'}</TableCell>
                      <TableCell>{email.bounce ?? '-'}</TableCell>
                      <TableCell>{email.id || '-'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
        
        {/* Load More Section - Right Aligned */}
        {deliverableEmails.length > 0 && (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 2,
            mb: 3, 
            width: '100%',
            maxWidth: '100%',
            overflowX: 'hidden'
          }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
              {Math.min(deliverableDisplayCount, deliverableEmails.length)} of {deliverableEmails.length}
            </Typography>
            
            {isLoadingMoreDeliverable ? (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center',
                gap: 1
              }}>
                <CircularProgress size={16} />
                <Typography variant="caption" color="text.secondary">
                  Loading...
                </Typography>
              </Box>
            ) : deliverableDisplayCount < deliverableEmails.length ? (
              <button 
                onClick={handleLoadMoreDeliverable}
                style={{
                  padding: '6px 16px',
                  backgroundColor: 'transparent',
                  color: '#1976d2',
                  border: '1px solid #1976d2',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: '500',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => {
                  (e.target as HTMLButtonElement).style.backgroundColor = '#1976d2';
                  (e.target as HTMLButtonElement).style.color = 'white';
                }}
                onMouseOut={(e) => {
                  (e.target as HTMLButtonElement).style.backgroundColor = 'transparent';
                  (e.target as HTMLButtonElement).style.color = '#1976d2';
                }}
              >
                Load More
              </button>
            ) : (
              <Typography variant="caption" color="text.secondary">
                No more records
              </Typography>
            )}
            </Box>
          )}
      </Box>

      {/* Risky Emails Table */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Risky ({riskyEmails.length})
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadRisky}
            disabled={isLoading}
          >
            Download
          </Button>
        </Box>
        <Paper sx={{ 
          flexShrink: 0,
          backgroundColor: 'white',
          width: '100%',
          maxWidth: '100%',
          overflowX: 'hidden',
          mb: 2
        }}>
          <TableContainer 
            sx={{ 
              maxHeight: 'none',
              overflowY: 'auto',
              overflowX: 'auto'
            }}
          >
            <Table 
              stickyHeader
              sx={{ 
                minWidth: 3000,
                '& .MuiTableCell-root': { 
                  padding: '6px 6px',
                  whiteSpace: 'nowrap'
                }
              }}
            >
              <TableHead>
                <TableRow>
                  {/* Match people table order: First Name, Last Name, Email first, then rest in order */}
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>First Name</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>Last Name</TableCell>
                  <TableCell sx={{ minWidth: 200, fontWeight: 'bold', backgroundColor: '#fff' }}>Email</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>Company Name</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>Title</TableCell>
                  <TableCell sx={{ minWidth: 200, fontWeight: 'bold', backgroundColor: '#fff' }}>Notes</TableCell>
                  <TableCell sx={{ minWidth: 300, fontWeight: 'bold', backgroundColor: '#fff' }}>Description</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>Unsubscribe</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>In Mailer</TableCell>
                  <TableCell sx={{ minWidth: 100, fontWeight: 'bold', backgroundColor: '#fff' }}>State</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>Location</TableCell>
                  <TableCell sx={{ minWidth: 200, fontWeight: 'bold', backgroundColor: '#fff' }}>California Location</TableCell>
                  <TableCell sx={{ minWidth: 100, fontWeight: 'bold', backgroundColor: '#fff' }}>Country</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>Area</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>Date Added</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>Last Emailed</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>Industry Tag</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>Industry Tag Two</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>Industry Tag Three</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>Industry Tag Four</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>Verification Status</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>Verification Date</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>LinkedIn ID</TableCell>
                  <TableCell sx={{ minWidth: 60, fontWeight: 'bold', backgroundColor: '#fff' }}>LI</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>LinkedIn Group ID</TableCell>
                  <TableCell sx={{ minWidth: 60, fontWeight: 'bold', backgroundColor: '#fff' }}>TW</TableCell>
                  <TableCell sx={{ minWidth: 60, fontWeight: 'bold', backgroundColor: '#fff' }}>FB</TableCell>
                  <TableCell sx={{ minWidth: 60, fontWeight: 'bold', backgroundColor: '#fff' }}>IG</TableCell>
                  <TableCell sx={{ minWidth: 140, fontWeight: 'bold', backgroundColor: '#fff' }}>Brand Follower</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>Username</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>P Tag 1</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>P Tag 2</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>P Tag 3</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>P Tag 4</TableCell>
                  <TableCell sx={{ minWidth: 100, fontWeight: 'bold', backgroundColor: '#fff' }}>Type</TableCell>
                  <TableCell sx={{ minWidth: 140, fontWeight: 'bold', backgroundColor: '#fff' }}>Company Type</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>License</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>Created At</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>Date Add in Mailer</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>Date Updated</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>Last Clicked</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>Last Opened</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>News THC</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>News CBD</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>News INV</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>News MED</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>Office Phone</TableCell>
                  <TableCell sx={{ minWidth: 100, fontWeight: 'bold', backgroundColor: '#fff' }}>Status</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>Leadleaper ID</TableCell>
                  <TableCell sx={{ minWidth: 100, fontWeight: 'bold', backgroundColor: '#fff' }}>B2B/B2C</TableCell>
                  <TableCell sx={{ minWidth: 100, fontWeight: 'bold', backgroundColor: '#fff' }}>Bounce</TableCell>
                  <TableCell sx={{ minWidth: 80, fontWeight: 'bold', backgroundColor: '#fff' }}>ID</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {riskyEmails.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={50} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No risky emails found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  riskyEmails.slice(0, riskyDisplayCount).map((email) => (
                    <TableRow key={email.id} hover>
                      <TableCell>{email.first_name || '-'}</TableCell>
                      <TableCell>{email.last_name || '-'}</TableCell>
                      <TableCell sx={{ fontWeight: 'medium' }}>{email.email || '-'}</TableCell>
                      <TableCell>{email.company_name || '-'}</TableCell>
                      <TableCell>{email.title || '-'}</TableCell>
                      <TableCell>
                        <Tooltip title={String(email.notes || '')} arrow>
                          <span>{truncateText(String(email.notes || ''), 30)}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={String(email.Description || '')} arrow>
                          <span>{truncateText(String(email.Description || ''), 30)}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>{email.unsubscribe ?? '-'}</TableCell>
                      <TableCell>
                        {email.in_mailer ? (
                          <Box sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', px: 1, py: 0.5, borderRadius: 1, display: 'inline-block', fontSize: '11px', fontWeight: 'bold' }}>
                            ADDED
                          </Box>
                        ) : (
                          <Box sx={{ bgcolor: '#f5f5f5', color: '#757575', px: 1, py: 0.5, borderRadius: 1, display: 'inline-block', fontSize: '11px' }}>
                            PENDING
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>{email.state_name || '-'}</TableCell>
                      <TableCell>{email.location || '-'}</TableCell>
                      <TableCell>{email.California_location || '-'}</TableCell>
                      <TableCell>{email.country_name || '-'}</TableCell>
                      <TableCell>{email.area || '-'}</TableCell>
                      <TableCell>{email.date_added || '-'}</TableCell>
                      <TableCell>{email.last_emailed || '-'}</TableCell>
                      <TableCell>{email.industry_name || email.industry_tag_name || '-'}</TableCell>
                      <TableCell>{email.industry_tag_two_name || '-'}</TableCell>
                      <TableCell>{email.industry_tag_three_name || '-'}</TableCell>
                      <TableCell>{email.industry_tag_four_name || '-'}</TableCell>
                      <TableCell>{email.verification_status_name || '-'}</TableCell>
                      <TableCell>{email.verification_date || '-'}</TableCell>
                      <TableCell>{email.linkedin_id || '-'}</TableCell>
                      <TableCell>{renderCompactLink(email.linkedin, 'linkedin')}</TableCell>
                      <TableCell>{email.linkedin_group_id || '-'}</TableCell>
                      <TableCell>{renderCompactLink(email.twitter, 'twitter')}</TableCell>
                      <TableCell>{renderCompactLink(email.facebook, 'facebook')}</TableCell>
                      <TableCell>{renderCompactLink(email.ig, 'ig')}</TableCell>
                      <TableCell>{email.brand_follower || '-'}</TableCell>
                      <TableCell>{email.username || '-'}</TableCell>
                      <TableCell>{email.p_tag_1_name || '-'}</TableCell>
                      <TableCell>{email.p_tag_2_name || '-'}</TableCell>
                      <TableCell>{email.p_tag_3_name || '-'}</TableCell>
                      <TableCell>{email.p_tag_4_name || '-'}</TableCell>
                      <TableCell>{email.type_ || '-'}</TableCell>
                      <TableCell>{email.company_type || '-'}</TableCell>
                      <TableCell>{email.license || '-'}</TableCell>
                      <TableCell>{email.created_at || '-'}</TableCell>
                      <TableCell>{email.date_add_in_mailer || '-'}</TableCell>
                      <TableCell>{email.date_updated || '-'}</TableCell>
                      <TableCell>{email.last_clicked || '-'}</TableCell>
                      <TableCell>{email.last_opened || '-'}</TableCell>
                      <TableCell>{email.news_thc ?? '-'}</TableCell>
                      <TableCell>{email.news_cbd ?? '-'}</TableCell>
                      <TableCell>{email.news_inv ?? '-'}</TableCell>
                      <TableCell>{email.news_med ?? '-'}</TableCell>
                      <TableCell>{email.oficine_phone || '-'}</TableCell>
                      <TableCell>{email.status || '-'}</TableCell>
                      <TableCell>{email.leadleaper_id || '-'}</TableCell>
                      <TableCell>{email.b2b_or_b2c || '-'}</TableCell>
                      <TableCell>{email.bounce ?? '-'}</TableCell>
                      <TableCell>{email.id || '-'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
        
        {/* Load More Section - Right Aligned */}
        {riskyEmails.length > 0 && (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 2,
            mb: 3, 
            width: '100%',
            maxWidth: '100%',
            overflowX: 'hidden'
          }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
              {Math.min(riskyDisplayCount, riskyEmails.length)} of {riskyEmails.length}
            </Typography>
            
            {isLoadingMoreRisky ? (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center',
                gap: 1
              }}>
                <CircularProgress size={16} />
                <Typography variant="caption" color="text.secondary">
                  Loading...
                </Typography>
              </Box>
            ) : riskyDisplayCount < riskyEmails.length ? (
              <button 
                onClick={handleLoadMoreRisky}
                style={{
                  padding: '6px 16px',
                  backgroundColor: 'transparent',
                  color: '#1976d2',
                  border: '1px solid #1976d2',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: '500',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => {
                  (e.target as HTMLButtonElement).style.backgroundColor = '#1976d2';
                  (e.target as HTMLButtonElement).style.color = 'white';
                }}
                onMouseOut={(e) => {
                  (e.target as HTMLButtonElement).style.backgroundColor = 'transparent';
                  (e.target as HTMLButtonElement).style.color = '#1976d2';
                }}
              >
                Load More
              </button>
            ) : (
              <Typography variant="caption" color="text.secondary">
                No more records
              </Typography>
            )}
          </Box>
        )}
      </Box>

      {/* Unknown Emails Table */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Unknown ({unknownEmails.length})
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadUnknown}
            disabled={isLoading}
          >
            Download
          </Button>
        </Box>
        <Paper sx={{ 
          flexShrink: 0,
          backgroundColor: 'white',
          width: '100%',
          maxWidth: '100%',
          overflowX: 'hidden',
          mb: 2
        }}>
          <TableContainer 
            sx={{ 
              maxHeight: 'none',
              overflowY: 'auto',
              overflowX: 'auto'
            }}
          >
            <Table 
              stickyHeader
              sx={{ 
                minWidth: 3000,
                '& .MuiTableCell-root': { 
                  padding: '6px 6px',
                  whiteSpace: 'nowrap'
                }
              }}
            >
              <TableHead>
                <TableRow>
                  {/* Match people table order: First Name, Last Name, Email first, then rest in order */}
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>First Name</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>Last Name</TableCell>
                  <TableCell sx={{ minWidth: 200, fontWeight: 'bold', backgroundColor: '#fff' }}>Email</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>Company Name</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>Title</TableCell>
                  <TableCell sx={{ minWidth: 200, fontWeight: 'bold', backgroundColor: '#fff' }}>Notes</TableCell>
                  <TableCell sx={{ minWidth: 300, fontWeight: 'bold', backgroundColor: '#fff' }}>Description</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>Unsubscribe</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>In Mailer</TableCell>
                  <TableCell sx={{ minWidth: 100, fontWeight: 'bold', backgroundColor: '#fff' }}>State</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>Location</TableCell>
                  <TableCell sx={{ minWidth: 200, fontWeight: 'bold', backgroundColor: '#fff' }}>California Location</TableCell>
                  <TableCell sx={{ minWidth: 100, fontWeight: 'bold', backgroundColor: '#fff' }}>Country</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>Area</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>Date Added</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>Last Emailed</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>Industry Tag</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>Industry Tag Two</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>Industry Tag Three</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>Industry Tag Four</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>Verification Status</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>Verification Date</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>LinkedIn ID</TableCell>
                  <TableCell sx={{ minWidth: 60, fontWeight: 'bold', backgroundColor: '#fff' }}>LI</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>LinkedIn Group ID</TableCell>
                  <TableCell sx={{ minWidth: 60, fontWeight: 'bold', backgroundColor: '#fff' }}>TW</TableCell>
                  <TableCell sx={{ minWidth: 60, fontWeight: 'bold', backgroundColor: '#fff' }}>FB</TableCell>
                  <TableCell sx={{ minWidth: 60, fontWeight: 'bold', backgroundColor: '#fff' }}>IG</TableCell>
                  <TableCell sx={{ minWidth: 140, fontWeight: 'bold', backgroundColor: '#fff' }}>Brand Follower</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>Username</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>P Tag 1</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>P Tag 2</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>P Tag 3</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>P Tag 4</TableCell>
                  <TableCell sx={{ minWidth: 100, fontWeight: 'bold', backgroundColor: '#fff' }}>Type</TableCell>
                  <TableCell sx={{ minWidth: 140, fontWeight: 'bold', backgroundColor: '#fff' }}>Company Type</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>License</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>Created At</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>Date Add in Mailer</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>Date Updated</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>Last Clicked</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>Last Opened</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>News THC</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>News CBD</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>News INV</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>News MED</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>Office Phone</TableCell>
                  <TableCell sx={{ minWidth: 100, fontWeight: 'bold', backgroundColor: '#fff' }}>Status</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>Leadleaper ID</TableCell>
                  <TableCell sx={{ minWidth: 100, fontWeight: 'bold', backgroundColor: '#fff' }}>B2B/B2C</TableCell>
                  <TableCell sx={{ minWidth: 100, fontWeight: 'bold', backgroundColor: '#fff' }}>Bounce</TableCell>
                  <TableCell sx={{ minWidth: 80, fontWeight: 'bold', backgroundColor: '#fff' }}>ID</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {unknownEmails.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={50} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No unknown emails found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  unknownEmails.slice(0, unknownDisplayCount).map((email) => (
                    <TableRow key={email.id} hover>
                      <TableCell>{email.first_name || '-'}</TableCell>
                      <TableCell>{email.last_name || '-'}</TableCell>
                      <TableCell sx={{ fontWeight: 'medium' }}>{email.email || '-'}</TableCell>
                      <TableCell>{email.company_name || '-'}</TableCell>
                      <TableCell>{email.title || '-'}</TableCell>
                      <TableCell>
                        <Tooltip title={String(email.notes || '')} arrow>
                          <span>{truncateText(String(email.notes || ''), 30)}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={String(email.Description || '')} arrow>
                          <span>{truncateText(String(email.Description || ''), 30)}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>{email.unsubscribe ?? '-'}</TableCell>
                      <TableCell>
                        {email.in_mailer ? (
                          <Box sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', px: 1, py: 0.5, borderRadius: 1, display: 'inline-block', fontSize: '11px', fontWeight: 'bold' }}>
                            ADDED
                          </Box>
                        ) : (
                          <Box sx={{ bgcolor: '#f5f5f5', color: '#757575', px: 1, py: 0.5, borderRadius: 1, display: 'inline-block', fontSize: '11px' }}>
                            PENDING
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>{email.state_name || '-'}</TableCell>
                      <TableCell>{email.location || '-'}</TableCell>
                      <TableCell>{email.California_location || '-'}</TableCell>
                      <TableCell>{email.country_name || '-'}</TableCell>
                      <TableCell>{email.area || '-'}</TableCell>
                      <TableCell>{email.date_added || '-'}</TableCell>
                      <TableCell>{email.last_emailed || '-'}</TableCell>
                      <TableCell>{email.industry_name || email.industry_tag_name || '-'}</TableCell>
                      <TableCell>{email.industry_tag_two_name || '-'}</TableCell>
                      <TableCell>{email.industry_tag_three_name || '-'}</TableCell>
                      <TableCell>{email.industry_tag_four_name || '-'}</TableCell>
                      <TableCell>{email.verification_status_name || '-'}</TableCell>
                      <TableCell>{email.verification_date || '-'}</TableCell>
                      <TableCell>{email.linkedin_id || '-'}</TableCell>
                      <TableCell>{renderCompactLink(email.linkedin, 'linkedin')}</TableCell>
                      <TableCell>{email.linkedin_group_id || '-'}</TableCell>
                      <TableCell>{renderCompactLink(email.twitter, 'twitter')}</TableCell>
                      <TableCell>{renderCompactLink(email.facebook, 'facebook')}</TableCell>
                      <TableCell>{renderCompactLink(email.ig, 'ig')}</TableCell>
                      <TableCell>{email.brand_follower || '-'}</TableCell>
                      <TableCell>{email.username || '-'}</TableCell>
                      <TableCell>{email.p_tag_1_name || '-'}</TableCell>
                      <TableCell>{email.p_tag_2_name || '-'}</TableCell>
                      <TableCell>{email.p_tag_3_name || '-'}</TableCell>
                      <TableCell>{email.p_tag_4_name || '-'}</TableCell>
                      <TableCell>{email.type_ || '-'}</TableCell>
                      <TableCell>{email.company_type || '-'}</TableCell>
                      <TableCell>{email.license || '-'}</TableCell>
                      <TableCell>{email.created_at || '-'}</TableCell>
                      <TableCell>{email.date_add_in_mailer || '-'}</TableCell>
                      <TableCell>{email.date_updated || '-'}</TableCell>
                      <TableCell>{email.last_clicked || '-'}</TableCell>
                      <TableCell>{email.last_opened || '-'}</TableCell>
                      <TableCell>{email.news_thc ?? '-'}</TableCell>
                      <TableCell>{email.news_cbd ?? '-'}</TableCell>
                      <TableCell>{email.news_inv ?? '-'}</TableCell>
                      <TableCell>{email.news_med ?? '-'}</TableCell>
                      <TableCell>{email.oficine_phone || '-'}</TableCell>
                      <TableCell>{email.status || '-'}</TableCell>
                      <TableCell>{email.leadleaper_id || '-'}</TableCell>
                      <TableCell>{email.b2b_or_b2c || '-'}</TableCell>
                      <TableCell>{email.bounce ?? '-'}</TableCell>
                      <TableCell>{email.id || '-'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
        
        {/* Load More Section - Right Aligned */}
        {unknownEmails.length > 0 && (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 2,
            mb: 3, 
            width: '100%',
            maxWidth: '100%',
            overflowX: 'hidden'
          }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
              {Math.min(unknownDisplayCount, unknownEmails.length)} of {unknownEmails.length}
            </Typography>
            
            {isLoadingMoreUnknown ? (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center',
                gap: 1
              }}>
                <CircularProgress size={16} />
                <Typography variant="caption" color="text.secondary">
                  Loading...
                </Typography>
              </Box>
            ) : unknownDisplayCount < unknownEmails.length ? (
              <button 
                onClick={handleLoadMoreUnknown}
                style={{
                  padding: '6px 16px',
                  backgroundColor: 'transparent',
                  color: '#1976d2',
                  border: '1px solid #1976d2',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: '500',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => {
                  (e.target as HTMLButtonElement).style.backgroundColor = '#1976d2';
                  (e.target as HTMLButtonElement).style.color = 'white';
                }}
                onMouseOut={(e) => {
                  (e.target as HTMLButtonElement).style.backgroundColor = 'transparent';
                  (e.target as HTMLButtonElement).style.color = '#1976d2';
                }}
              >
                Load More
              </button>
            ) : (
              <Typography variant="caption" color="text.secondary">
                No more records
              </Typography>
            )}
            </Box>
          )}
      </Box>

      {/* Undeliverable Emails Table */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Undeliverable ({undeliverableEmails.length})
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadUndeliverable}
            disabled={isLoading}
          >
            Download
          </Button>
        </Box>
        <Paper sx={{ 
          flexShrink: 0,
          backgroundColor: 'white',
          width: '100%',
          maxWidth: '100%',
          overflowX: 'hidden',
          mb: 2
        }}>
          <TableContainer 
            sx={{ 
              maxHeight: 'none',
              overflowY: 'auto',
              overflowX: 'auto'
            }}
          >
            <Table 
              stickyHeader
              sx={{ 
                minWidth: 3000,
                '& .MuiTableCell-root': { 
                  padding: '6px 6px',
                  whiteSpace: 'nowrap'
                }
              }}
            >
              <TableHead>
                <TableRow>
                  {/* Match people table order: First Name, Last Name, Email first, then rest in order */}
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>First Name</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>Last Name</TableCell>
                  <TableCell sx={{ minWidth: 200, fontWeight: 'bold', backgroundColor: '#fff' }}>Email</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>Company Name</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>Title</TableCell>
                  <TableCell sx={{ minWidth: 200, fontWeight: 'bold', backgroundColor: '#fff' }}>Notes</TableCell>
                  <TableCell sx={{ minWidth: 300, fontWeight: 'bold', backgroundColor: '#fff' }}>Description</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>Unsubscribe</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>In Mailer</TableCell>
                  <TableCell sx={{ minWidth: 100, fontWeight: 'bold', backgroundColor: '#fff' }}>State</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>Location</TableCell>
                  <TableCell sx={{ minWidth: 200, fontWeight: 'bold', backgroundColor: '#fff' }}>California Location</TableCell>
                  <TableCell sx={{ minWidth: 100, fontWeight: 'bold', backgroundColor: '#fff' }}>Country</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>Area</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>Date Added</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>Last Emailed</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>Industry Tag</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>Industry Tag Two</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>Industry Tag Three</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>Industry Tag Four</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>Verification Status</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>Verification Date</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>LinkedIn ID</TableCell>
                  <TableCell sx={{ minWidth: 60, fontWeight: 'bold', backgroundColor: '#fff' }}>LI</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>LinkedIn Group ID</TableCell>
                  <TableCell sx={{ minWidth: 60, fontWeight: 'bold', backgroundColor: '#fff' }}>TW</TableCell>
                  <TableCell sx={{ minWidth: 60, fontWeight: 'bold', backgroundColor: '#fff' }}>FB</TableCell>
                  <TableCell sx={{ minWidth: 60, fontWeight: 'bold', backgroundColor: '#fff' }}>IG</TableCell>
                  <TableCell sx={{ minWidth: 140, fontWeight: 'bold', backgroundColor: '#fff' }}>Brand Follower</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>Username</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>P Tag 1</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>P Tag 2</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>P Tag 3</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>P Tag 4</TableCell>
                  <TableCell sx={{ minWidth: 100, fontWeight: 'bold', backgroundColor: '#fff' }}>Type</TableCell>
                  <TableCell sx={{ minWidth: 140, fontWeight: 'bold', backgroundColor: '#fff' }}>Company Type</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>License</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>Created At</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>Date Add in Mailer</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>Date Updated</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>Last Clicked</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>Last Opened</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>News THC</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>News CBD</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>News INV</TableCell>
                  <TableCell sx={{ minWidth: 120, fontWeight: 'bold', backgroundColor: '#fff' }}>News MED</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>Office Phone</TableCell>
                  <TableCell sx={{ minWidth: 100, fontWeight: 'bold', backgroundColor: '#fff' }}>Status</TableCell>
                  <TableCell sx={{ minWidth: 150, fontWeight: 'bold', backgroundColor: '#fff' }}>Leadleaper ID</TableCell>
                  <TableCell sx={{ minWidth: 100, fontWeight: 'bold', backgroundColor: '#fff' }}>B2B/B2C</TableCell>
                  <TableCell sx={{ minWidth: 100, fontWeight: 'bold', backgroundColor: '#fff' }}>Bounce</TableCell>
                  <TableCell sx={{ minWidth: 80, fontWeight: 'bold', backgroundColor: '#fff' }}>ID</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {undeliverableEmails.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={50} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No undeliverable emails found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  undeliverableEmails.slice(0, undeliverableDisplayCount).map((email) => (
                    <TableRow key={email.id} hover>
                      <TableCell>{email.first_name || '-'}</TableCell>
                      <TableCell>{email.last_name || '-'}</TableCell>
                      <TableCell sx={{ fontWeight: 'medium' }}>{email.email || '-'}</TableCell>
                      <TableCell>{email.company_name || '-'}</TableCell>
                      <TableCell>{email.title || '-'}</TableCell>
                      <TableCell>
                        <Tooltip title={String(email.notes || '')} arrow>
                          <span>{truncateText(String(email.notes || ''), 30)}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={String(email.Description || '')} arrow>
                          <span>{truncateText(String(email.Description || ''), 30)}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>{email.unsubscribe ?? '-'}</TableCell>
                      <TableCell>
                        {email.in_mailer ? (
                          <Box sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', px: 1, py: 0.5, borderRadius: 1, display: 'inline-block', fontSize: '11px', fontWeight: 'bold' }}>
                            ADDED
                          </Box>
                        ) : (
                          <Box sx={{ bgcolor: '#f5f5f5', color: '#757575', px: 1, py: 0.5, borderRadius: 1, display: 'inline-block', fontSize: '11px' }}>
                            PENDING
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>{email.state_name || '-'}</TableCell>
                      <TableCell>{email.location || '-'}</TableCell>
                      <TableCell>{email.California_location || '-'}</TableCell>
                      <TableCell>{email.country_name || '-'}</TableCell>
                      <TableCell>{email.area || '-'}</TableCell>
                      <TableCell>{email.date_added || '-'}</TableCell>
                      <TableCell>{email.last_emailed || '-'}</TableCell>
                      <TableCell>{email.industry_name || email.industry_tag_name || '-'}</TableCell>
                      <TableCell>{email.industry_tag_two_name || '-'}</TableCell>
                      <TableCell>{email.industry_tag_three_name || '-'}</TableCell>
                      <TableCell>{email.industry_tag_four_name || '-'}</TableCell>
                      <TableCell>{email.verification_status_name || '-'}</TableCell>
                      <TableCell>{email.verification_date || '-'}</TableCell>
                      <TableCell>{email.linkedin_id || '-'}</TableCell>
                      <TableCell>{renderCompactLink(email.linkedin, 'linkedin')}</TableCell>
                      <TableCell>{email.linkedin_group_id || '-'}</TableCell>
                      <TableCell>{renderCompactLink(email.twitter, 'twitter')}</TableCell>
                      <TableCell>{renderCompactLink(email.facebook, 'facebook')}</TableCell>
                      <TableCell>{renderCompactLink(email.ig, 'ig')}</TableCell>
                      <TableCell>{email.brand_follower || '-'}</TableCell>
                      <TableCell>{email.username || '-'}</TableCell>
                      <TableCell>{email.p_tag_1_name || '-'}</TableCell>
                      <TableCell>{email.p_tag_2_name || '-'}</TableCell>
                      <TableCell>{email.p_tag_3_name || '-'}</TableCell>
                      <TableCell>{email.p_tag_4_name || '-'}</TableCell>
                      <TableCell>{email.type_ || '-'}</TableCell>
                      <TableCell>{email.company_type || '-'}</TableCell>
                      <TableCell>{email.license || '-'}</TableCell>
                      <TableCell>{email.created_at || '-'}</TableCell>
                      <TableCell>{email.date_add_in_mailer || '-'}</TableCell>
                      <TableCell>{email.date_updated || '-'}</TableCell>
                      <TableCell>{email.last_clicked || '-'}</TableCell>
                      <TableCell>{email.last_opened || '-'}</TableCell>
                      <TableCell>{email.news_thc ?? '-'}</TableCell>
                      <TableCell>{email.news_cbd ?? '-'}</TableCell>
                      <TableCell>{email.news_inv ?? '-'}</TableCell>
                      <TableCell>{email.news_med ?? '-'}</TableCell>
                      <TableCell>{email.oficine_phone || '-'}</TableCell>
                      <TableCell>{email.status || '-'}</TableCell>
                      <TableCell>{email.leadleaper_id || '-'}</TableCell>
                      <TableCell>{email.b2b_or_b2c || '-'}</TableCell>
                      <TableCell>{email.bounce ?? '-'}</TableCell>
                      <TableCell>{email.id || '-'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
        
        {/* Load More Section - Right Aligned */}
        {undeliverableEmails.length > 0 && (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 2,
            mb: 3, 
            width: '100%',
            maxWidth: '100%',
            overflowX: 'hidden'
          }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
              {Math.min(undeliverableDisplayCount, undeliverableEmails.length)} of {undeliverableEmails.length}
            </Typography>
            
            {isLoadingMoreUndeliverable ? (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center',
                gap: 1
              }}>
                <CircularProgress size={16} />
                <Typography variant="caption" color="text.secondary">
                  Loading...
                </Typography>
              </Box>
            ) : undeliverableDisplayCount < undeliverableEmails.length ? (
              <button 
                onClick={handleLoadMoreUndeliverable}
                style={{
                  padding: '6px 16px',
                  backgroundColor: 'transparent',
                  color: '#1976d2',
                  border: '1px solid #1976d2',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: '500',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => {
                  (e.target as HTMLButtonElement).style.backgroundColor = '#1976d2';
                  (e.target as HTMLButtonElement).style.color = 'white';
                }}
                onMouseOut={(e) => {
                  (e.target as HTMLButtonElement).style.backgroundColor = 'transparent';
                  (e.target as HTMLButtonElement).style.color = '#1976d2';
                }}
              >
                Load More
              </button>
            ) : (
              <Typography variant="caption" color="text.secondary">
                No more records
              </Typography>
            )}
          </Box>
        )}
      </Box>

      {/* MailWhiz Modal */}
      <Dialog open={showMailWhizModal} onClose={() => !isMailWhizProcessing && setShowMailWhizModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ borderBottom: '1px solid #eee', pb: 2 }}>
          {mailWhizMode === 'sync' ? 'Sync Deliverable Emails to List' : 'Send Transactional Intro Email'}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f7fa', borderRadius: 2 }}>
            <Typography variant="body2">
              Emails to process: <strong>{deliverableEmails.length.toLocaleString()}</strong>
            </Typography>
            <Typography variant="caption" color="text.secondary">
              This will process all deliverable emails in the current view.
            </Typography>
          </Box>

          {mailWhizResult ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              Successfully processed! Check MailWhiz for details.
              <pre style={{ fontSize: '10px', marginTop: '10px', overflowX: 'auto' }}>
                {JSON.stringify(mailWhizResult, null, 2)}
              </pre>
            </Alert>
          ) : (
            <Grid container spacing={3}>
              {mailWhizMode === 'sync' ? (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>MailWhiz List UID</Typography>
                  <input 
                    type="text" 
                    value={mailWhizListId} 
                    onChange={(e) => setMailWhizListId(e.target.value)}
                    style={{ 
                      width: '100%', 
                      padding: '12px', 
                      border: '1px solid #ccc', 
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                    placeholder="Enter List UID (e.g. ab123cd456)"
                  />
                </Grid>
              ) : (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>Select Email Template</Typography>
                  <select
                    value={selectedTemplate}
                    onChange={(e) => setSelectedTemplate(e.target.value)}
                    style={{ 
                      width: '100%', 
                      padding: '12px', 
                      border: '1px solid #ccc', 
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  >
                    <option value="">-- Select Template --</option>
                    {mailWhizTemplates.map((tpl: any) => (
                      <option key={tpl.template_uid} value={tpl.template_uid}>
                        {tpl.name} ({tpl.template_uid})
                      </option>
                    ))}
                  </select>
                  {mailWhizTemplates.length === 0 && (
                    <Typography variant="caption" color="error">
                      No templates found or API not configured.
                    </Typography>
                  )}
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #eee' }}>
          <Button onClick={() => setShowMailWhizModal(false)} disabled={isMailWhizProcessing}>
            {mailWhizResult ? 'CLOSE' : 'CANCEL'}
          </Button>
          {!mailWhizResult && (
            <Button 
              onClick={handleMailWhizAction} 
              variant="contained" 
              color={mailWhizMode === 'sync' ? 'primary' : 'secondary'}
              disabled={isMailWhizProcessing || (mailWhizMode === 'intro' && !selectedTemplate) || (mailWhizMode === 'sync' && !mailWhizListId)}
              startIcon={isMailWhizProcessing && <CircularProgress size={20} color="inherit" />}
            >
              {isMailWhizProcessing ? 'PROCESSING...' : (mailWhizMode === 'sync' ? 'SYNC NOW' : 'SEND INTRO')}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OutreachEmailResultsPage;
